"use client";

import React, { useRef, useState } from "react";
import { Link } from "@/i18n/navigation";
import {
  MdArrowForward,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
} from "react-icons/md";
import { useLocale } from "next-intl";

import styles from "./recommended-listings.module.css";
import { ListingCardData } from "@/types/types";
import HomeListingCard from "@/components-v2/listing-cards/home-listing-card";

interface RecommendedListingsProps {
  listings: ListingCardData[];
}

export default function RecommendedListings({
  listings,
}: RecommendedListingsProps) {
  const locale = useLocale();
  const isRTL = locale === "ar" || locale === "he";

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  if (!listings || listings.length === 0) return null;

  // In RTL mode, the scroll axis is reversed natively by the browser
  const scrollAmount = isRTL ? -400 : 400;

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;

    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  return (
    <div className={styles.outerBackground}>
      <section className={styles.section}>
        {/* Header Area */}
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.title}>Recommended For You</h2>
            <p className={styles.subtitle}>
              Properties we think you&apos;ll love.
            </p>
          </div>
          <Link href="/recommendations" className={styles.viewAllLink}>
            View all recommendations
            <MdArrowForward className={styles.linkIcon} />
          </Link>
        </div>

        {/* Native Drag-to-Scroll Track Area */}
        <div className={styles.sliderWrapper}>
          <div
            className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            dir={isRTL ? "rtl" : "ltr"} /* Essential for RTL scroll logic */
          >
            {listings.map((listing) => (
              <div
                key={listing.listing_id}
                className={styles.cardWrapper}
                onClickCapture={(e) => {
                  // Prevents the internal link from triggering if dragging
                  if (hasDragged) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                <HomeListingCard data={listing} />
              </div>
            ))}
          </div>

          {/* Carousel Controls */}
          <div className={styles.carouselControls}>
            <button
              className={styles.controlBtn}
              aria-label="Previous"
              onClick={handleScrollLeft}
            >
              <MdOutlineChevronLeft className={styles.controlIcon} />
            </button>
            <button
              className={styles.controlBtn}
              aria-label="Next"
              onClick={handleScrollRight}
            >
              <MdOutlineChevronRight className={styles.controlIcon} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
